import React, { useState } from "react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe } from "lucide-react";
import axios from "axios";
import { CONTACT_INFO, SERVICE_OPTIONS } from "../data/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact/`, {
        ...formData,
        subject: formData.service || "Project Inquiry",
        source: "website",
      });
      toast.success("Thank you! Our team will reach out within one business day.");
      setFormData({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      console.error(error);
      toast.error("Failed to send. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="bg-slate-900 text-white pt-36 pb-20 relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">Get in Touch</span>
          <h1 className="text-5xl font-display font-bold uppercase tracking-wide mb-4">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-2xl font-light">
            Partner with us for integrated industrial solutions and expert consultancy.
          </p>
        </div>
      </div>

      <main className="flex-grow py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-display font-bold text-foreground mb-6">Headquarters</h2>
                <p className="text-gray-600 mb-8">
                  Reach out for project inquiries, consultations, or partnership opportunities. We undertake projects across India in mining, steel and industrial sectors.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-gold/15 p-3 rounded-sm text-gold"><MapPin size={24} /></div>
                    <div>
                      <h4 className="font-bold text-foreground">Address</h4>
                      <p className="text-gray-600">Yantraksh Projects Pvt. Ltd.<br />{CONTACT_INFO.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-gold/15 p-3 rounded-sm text-gold"><Phone size={24} /></div>
                    <div>
                      <h4 className="font-bold text-foreground">Phone</h4>
                      <a href={`tel:${CONTACT_INFO.phone}`} className="text-gray-600 hover:text-primary">{CONTACT_INFO.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-gold/15 p-3 rounded-sm text-gold"><Mail size={24} /></div>
                    <div>
                      <h4 className="font-bold text-foreground">Email</h4>
                      <a href={`mailto:${CONTACT_INFO.email}`} className="text-gray-600 hover:text-primary">{CONTACT_INFO.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-gold/15 p-3 rounded-sm text-gold"><Globe size={24} /></div>
                    <div>
                      <h4 className="font-bold text-foreground">Website</h4>
                      <p className="text-gray-600">{CONTACT_INFO.website}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="bg-gray-50 p-10 border-t-4 border-gold shadow-lg">
              <h2 className="text-2xl font-display font-bold text-foreground mb-2">Partner With Us</h2>
              <p className="text-gray-500 text-sm mb-6">Tell us about your project and we'll get back within one business day.</p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="contact-name" placeholder="Your Name" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-white rounded-none focus-visible:ring-primary" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" data-testid="contact-company" placeholder="Company Name" value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="bg-white rounded-none focus-visible:ring-primary" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" data-testid="contact-email" type="email" placeholder="email@company.com" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-white rounded-none focus-visible:ring-primary" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" data-testid="contact-phone" placeholder="+91 ..." value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white rounded-none focus-visible:ring-primary" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">Service Required</Label>
                  <Select value={formData.service} onValueChange={(v) => setFormData({ ...formData, service: v })}>
                    <SelectTrigger data-testid="contact-service" className="bg-white rounded-none focus:ring-primary">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Project Details *</Label>
                  <Textarea id="message" data-testid="contact-message" placeholder="Tell us about your project, location and timeline..."
                    className="min-h-[130px] bg-white rounded-none focus-visible:ring-primary" value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })} required />
                </div>

                <Button type="submit" data-testid="contact-submit" size="lg" disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white rounded-none uppercase tracking-widest">
                  {loading ? "Sending..." : "Partner With Us"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
